import React, { useEffect, useState } from "react";
import { useCustomerAccount } from "../AccountProvider";
import EmailSection from "./EmailSection/EmailSection";
import PhoneSection from "./PhoneSection/PhoneSection";
import { find } from "lodash";
import { useCommonContext } from "../CommonProvider";
import { MESSAGE_TYPE } from "../../../constants/messages";
import { MESSAGE } from "../../../constants";
import analytics from "../../../utils/analytics";
import TwoFactorAuth from "./TwoFactorAuth";
import {isUserB2B} from "../../../utils";
import PasswordSection from "./PasswordSection/PasswordSection";
import PasswordOptionsTiles from "./OptionsToggle/PasswordOptions.component";
import { performCleansingOfWebAuthnLocalStorage } from "../../../utils/webauthnUtils";
import {ErrorBoundary} from "@thd-olt-component-react/error-boundary";
import {Typography} from "@one-thd/sui-atomic-components";
import {PasswordContext} from "../../../contexts/PasswordContext";
import NativeWebAuthnProvider from "../NativeWebAuthnProvider";


const DISPLAY_MODE = "DISPLAY_MODE";
const EMAIL_MODE = "EMAIL_MODE";
const PASSWORD_MODE = "PASSWORD_MODE";
const PHONE_MODE = "PHONE_MODE";

const SignInDetails = () => {
  const context = useCustomerAccount();
  const phones = context?.account?.profilePhones?.phone || [];
  const verifiedPhone = find(phones, (ph) => ph?.primaryFlag === "Y" && ph?.phoneVerified === "Y");
  const phoneNumber = verifiedPhone?.number || find(phones, (ph) => ph?.primaryFlag === "Y")?.number;
  const isVerified = verifiedPhone?.phoneVerified === "Y" || false;
  const { twoFactorAuthOptIn } = context?.account?.profile || {};
  const isUserPasswordlessEnrolled = context?.account?.isPwdLessEnroll === true;
  const is2FA = twoFactorAuthOptIn === "Y";
  const phone = { phoneNumber, isVerified };
  const { emailId } = context?.account?.profile || {};
  const { addAlert, removeAlert } = useCommonContext();
  const [mode, setMode] = useState(DISPLAY_MODE);
  const [ passwordOptions, setPasswordOptions ] = useState(context?.account?.isPwdLessEnroll === true ? "passwordless" : "password");

  useEffect(() => {
    if (!phoneNumber && !isUserB2B()) {
      addAlert(MESSAGE_TYPE.INFORMATION, MESSAGE.SECURITY_MESSAGE_NO_PHONE_BEST, true);
    } else if (!is2FA && !isUserB2B() && !isUserPasswordlessEnrolled && !verifiedPhone) {
      addAlert(MESSAGE_TYPE.INFORMATION, MESSAGE.INVITE_TO_VERIFY_PHONE);
    } else if (!is2FA && verifiedPhone && !isUserB2B() && !isUserPasswordlessEnrolled) {
      addAlert(MESSAGE_TYPE.INFORMATION, MESSAGE.VERIFIED_PHONE_INVITE_2);
    }
    performCleansingOfWebAuthnLocalStorage();
  }, []);

  const onDoneEditing = () => setMode(DISPLAY_MODE);
  const onEditEmail = () => setMode(EMAIL_MODE);
  const onEditPhone = () => setMode(PHONE_MODE);
  const onEditPassword = () => {
    setMode(PASSWORD_MODE);
    analytics.postSettingsSubmissionEvent("security:password edit initiated");
  }

  const onPhoneVerifySuccess = (verifiedExistingNumber) => {
    removeAlert();
    addAlert(MESSAGE_TYPE.SUCCESS, verifiedExistingNumber ? MESSAGE.PHONE_VERIFY_SUCCESS : MESSAGE.PHONE_ADD_AND_VERIFY_SUCCESS);
  }

  const onVerifiedPhoneChangeSuccess = () => {
    removeAlert();
    addAlert(MESSAGE_TYPE.SUCCESS, "Well done! You successfully changed and verified your phone number.");
  }

  const onUnverifiedPhoneChangeSuccess = () => {
    removeAlert();
    addAlert(MESSAGE_TYPE.SUCCESS, "Well done! You successfully changed and verified your phone number.");
  }

  const onEmailChangeSuccess = () => {
    removeAlert();
    addAlert(MESSAGE_TYPE.SUCCESS, "Well done! You successfully changed your email address.");
  }

  const on2FAChange = (isNow2FA, remove2FABanner) => {
    removeAlert();
    if(isNow2FA) {
      addAlert(MESSAGE_TYPE.SUCCESS, "Well done! You successfully enabled Two-Factor Authentication.");
      remove2FABanner(null);
    } else {
      addAlert(MESSAGE_TYPE.SUCCESS, "Well done! You successfully disabled Two-Factor Authentication.");
      remove2FABanner(null);
    }
  }

  return (
    <ErrorBoundary name="sign-in-details">
    <section className="sui-grid sui-grid-cols-12">
      <div className="sui-col-span-12">
        <Typography weight="display" variant="h2" uppercase>
          Sign In Details
        </Typography>
        <EmailSection email={emailId} onEditEmail={onEditEmail} isEditingEmail={mode === EMAIL_MODE}
                      onDoneEditing={onDoneEditing} onEmailChangeSuccess={onEmailChangeSuccess}/>
        <PasswordContext.Provider value={{passwordOptions, setPasswordOptions}}>
          <NativeWebAuthnProvider>
            <PasswordOptionsTiles email={emailId} isUserPasswordlessEnrolled={isUserPasswordlessEnrolled} />
            <PasswordSection onEditPassword={onEditPassword} isEditingPassword={mode === PASSWORD_MODE}
                             onDoneEditing={onDoneEditing} isUserPasswordless={isUserPasswordlessEnrolled}/>
          </NativeWebAuthnProvider>
        </PasswordContext.Provider>
        <PhoneSection isEditingPhone={mode === PHONE_MODE}
                      onEditPhone={onEditPhone} onDoneEditing={onDoneEditing}
                      onVerifiedPhoneChangeSuccess={onVerifiedPhoneChangeSuccess}
                      onUnverifiedPhoneChangeSuccess={onUnverifiedPhoneChangeSuccess}
                      onPhoneChangeSuccess={onVerifiedPhoneChangeSuccess} phone={phone} currentEmail={emailId}
                      onVerificationSuccess={onPhoneVerifySuccess}/>
        <TwoFactorAuth updateBanner={on2FAChange} isUserPasswordlessEnrolled={isUserPasswordlessEnrolled} />
      </div>
    </section>
    </ErrorBoundary>
  );
}

export default SignInDetails;